export interface InfinitePage<T> { data: T[]; totalRecords: number; }

/** Serial, bounded page requests. A reset invalidates any outstanding response immediately. */
export class SlickGridInfinitePager<Q, T> {
	rows: T[] = [];
	totalRecords = 0;
	nextPage = 1;
	exhausted = false;
	error = false;
	revision = 0;
	private query: Q;
	private flight: Promise<boolean> = null;

	get loading(): boolean { return !!this.flight; }
	get ready(): boolean { return this.query != null; }

	constructor(private fetch: (query: Q, page: number) => Promise<InfinitePage<T>>,
		private key: (row: T) => unknown, private currentRows?: () => T[]) { }

	reset(query: Q): Promise<boolean> {
		const revision = ++this.revision;
		this.query = query;
		this.rows = [];
		this.totalRecords = 0;
		this.nextPage = 1;
		this.exhausted = this.error = false;
		// Wait for the stale request to finish rather than issuing simultaneous requests.
		if (this.flight) return this.flight.then(() =>
			revision === this.revision ? this.requestNext() : false);
		return this.requestNext();
	}

	invalidate(): void { ++this.revision; this.query = null; }

	retry(): Promise<boolean> { this.error = false; return this.requestNext(); }

	requestNext(): Promise<boolean> {
		if (this.flight) return this.flight;
		if (!this.ready || this.exhausted || this.error) return Promise.resolve(false);
		const revision = this.revision;
		const query = this.query;
		const page = this.nextPage;
		this.flight = (async () => {
			try {
				const response = await this.fetch(query, page);
				if (revision !== this.revision) return false;
				if (!Array.isArray(response.data) || !Number.isFinite(response.totalRecords) || response.totalRecords < 0) throw new Error('Invalid page');
				this.rows = this.currentRows ? this.currentRows() : this.rows;
				const seen = new Set(this.rows.map(row => this.key(row)));
				let added = 0;
				response.data.forEach(row => {
					const id = this.key(row);
					if (!seen.has(id)) { seen.add(id); this.rows.push(row); added++; }
				});
				// A repeated page must not cause an endless fill loop or count duplicates as loaded records.
				if (response.data.length && !added) throw new Error('Page made no progress');
				this.totalRecords = response.totalRecords;
				this.nextPage = page + 1;
				this.exhausted = !response.data.length || this.rows.length >= response.totalRecords;
				return true;
			} catch {
				if (revision === this.revision) this.error = true;
				return false;
			} finally { this.flight = null; }
		})();
		return this.flight;
	}
}
